import assert from "assert"
import { GLTF } from "three/examples/jsm/Addons.js"
import { color, positionLocal, texture, uniform, vec2, vec3 } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

type NeonSettings = {
  color: THREE.UniformNode<THREE.Color>
  emissiveIntensity: THREE.UniformNode<number>
  emissiveNode: THREE.Node
}

const NAMES = {
  BUILDING: {
    NEON1: "building-l-neon1",
    NEON2: "building-l-neon2",
    NEON3: "building-l-neon3",
    NEON4: "building-l-neon4",
  },
  TOWER: {
    LEFTLEFT: {
      NEON: "tower-ll-neon",
    },
    LEFT: {
      SIGN: "tower-l-sign",
      LINE: {
        LEFT: "tower-l-line-l",
        RIGHT: "tower-l-line-r",
      },
    },
    RIGHT: {
      SIGN: "tower-r-sign",
      LINE: {
        LEFT: "tower-r-line-l",
        RIGHT: "tower-r-line-r",
      },
    },
  },
}

/**
 * Recursively extracts all string values from a nested object structure
 */
function extractNames(obj: unknown): string[] {
  const names: string[] = []
  if (typeof obj !== "object" || obj === null) {
    return names
  }
  for (const value of Object.values(obj)) {
    if (typeof value === "string") {
      names.push(value)
    } else if (typeof value === "object" && value !== null) {
      names.push(...extractNames(value))
    }
  }
  return names
}

/**
 * Returns unique color and emissive intensity values for each object name
 */
function getNeonColors(name: string): {
  color: string
  emissiveIntensity: number
} {
  const colorMap: Record<string, { color: string; emissiveIntensity: number }> =
    {
      [NAMES.BUILDING.NEON1]: { color: "#272c5f", emissiveIntensity: 0.1 },
      [NAMES.BUILDING.NEON2]: { color: "#272c5f", emissiveIntensity: 0.1 },
      [NAMES.BUILDING.NEON3]: { color: "#272c5f", emissiveIntensity: 0.1 },
      [NAMES.BUILDING.NEON4]: { color: "#3b3e46", emissiveIntensity: 0.1 },
      [NAMES.TOWER.LEFTLEFT.NEON]: { color: "#314372", emissiveIntensity: 0.1 },
      [NAMES.TOWER.LEFT.SIGN]: { color: "#272c5f", emissiveIntensity: 0.1 },
      [NAMES.TOWER.LEFT.LINE.LEFT]: {
        color: "#272c5f",
        emissiveIntensity: 0.6,
      },
      [NAMES.TOWER.LEFT.LINE.RIGHT]: {
        color: "#272c5f",
        emissiveIntensity: 0.7,
      },
      [NAMES.TOWER.RIGHT.SIGN]: { color: "#141416", emissiveIntensity: 0.5 },
      [NAMES.TOWER.RIGHT.LINE.LEFT]: {
        color: "#272c5f",
        emissiveIntensity: 0.6,
      },
      [NAMES.TOWER.RIGHT.LINE.RIGHT]: {
        color: "#272c5f",
        emissiveIntensity: 0.7,
      },
    }

  // Default fallback if name not in map
  return (
    colorMap[name] ?? {
      color: "#305d75",
      emissiveIntensity: 0.5,
    }
  )
}

export default class Neon {
  scene: THREE.Object3D
  debugFolder?: FolderApi
  neonDebugFolder?: FolderApi
  resources: { [key: string]: GLTF }
  neonSettings: Map<string, NeonSettings> = new Map()
  textureResources?: { [key: string]: THREE.Texture }

  constructor({
    scene,
    debugFolder,
    resources,
    textureResources,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    resources: { [key: string]: GLTF }
    textureResources?: { [key: string]: THREE.Texture }
  }) {
    this.scene = scene
    this.debugFolder = debugFolder
    this.resources = resources
    this.textureResources = textureResources

    this.setDebug()
    this.createMesh()
  }

  createMesh() {
    // Extract all object names from the NAMES structure
    const allNames = extractNames(NAMES)

    // Process each object
    for (const name of allNames) {
      const object = this.resources.blender.scene?.getObjectByName(name)

      if (!object) {
        console.warn(`Object "${name}" not found in scene`)
        continue
      }

      assert(object instanceof THREE.Mesh, `${name} must be a Mesh`)

      const { color: colorHex, emissiveIntensity: intensity } =
        getNeonColors(name)

      const colorNode = uniform(color(colorHex))
      const emissiveIntensityNode = uniform(intensity)
      const emissiveNode = colorNode.mul(emissiveIntensityNode)

      const material = new THREE.MeshBasicNodeMaterial()
      material.side = THREE.DoubleSide // Ensure texture is visible from both sides

      // Apply texture for tower-l-sign
      if (name === NAMES.TOWER.LEFT.SIGN && this.textureResources) {
        const signTexture = this.textureResources["tower-l-sign"]
        signTexture.wrapS = THREE.RepeatWrapping
        signTexture.wrapT = THREE.RepeatWrapping
        if (signTexture && signTexture instanceof THREE.Texture) {
          // Ensure texture is properly configured
          signTexture.colorSpace = THREE.SRGBColorSpace
          signTexture.needsUpdate = true

          const settings: NeonSettings = {
            color: colorNode,
            emissiveIntensity: emissiveIntensityNode,
            emissiveNode: emissiveNode,
          }
          this.neonSettings.set(name, settings)

          // Sample texture using position-based UV mapping
          const textureSample = texture(
            signTexture,
            positionLocal.xz.mul(vec2(0.3, -0.3)).add(0.5),
          )
          const textureNode = textureSample.rgb

          material.colorNode = textureNode.mul(vec3(0.2, 0.1, 0.1))
          ;(
            material as unknown as THREE.MeshPhysicalNodeMaterial
          ).emissiveNode = textureNode.mul(settings.emissiveIntensity)
        } else {
          console.log("Texture not found or not a Texture instance")
          const settings: NeonSettings = {
            color: colorNode,
            emissiveIntensity: emissiveIntensityNode,
            emissiveNode: emissiveNode,
          }
          this.neonSettings.set(name, settings)
          material.colorNode = settings.color
          ;(
            material as unknown as THREE.MeshPhysicalNodeMaterial
          ).emissiveNode = settings.emissiveNode
        }
      } else {
        const settings: NeonSettings = {
          color: colorNode,
          emissiveIntensity: emissiveIntensityNode,
          emissiveNode: emissiveNode,
        }
        this.neonSettings.set(name, settings)
        material.colorNode = settings.color
        ;(material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
          settings.emissiveNode
      }

      object.material = material
      this.setNeonDebug(name, object)

      this.scene.add(object)
    }
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    this.neonDebugFolder = this.debugFolder.addFolder({
      title: "💡 Neon",
      expanded: false,
    })
  }

  setNeonDebug(
    name: string,
    _object: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicNodeMaterial>,
  ) {
    if (!this.neonDebugFolder) {
      return
    }

    const settings = this.neonSettings.get(name)
    if (!settings) {
      return
    }

    const folder = this.neonDebugFolder.addFolder({
      title: `💡 ${name}`,
      expanded: false,
    })

    const colorValue = settings.color.value as THREE.Color
    const emissiveIntensityValue = settings.emissiveIntensity.value as number

    const debugSettings = {
      color: `#${colorValue.getHexString()}`,
      emissiveIntensity: emissiveIntensityValue,
    }

    folder
      .addBinding(debugSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        ;(settings.color.value as THREE.Color) = new THREE.Color(ev.value)
        // Update emissiveNode when color changes
        settings.emissiveNode = settings.color.mul(settings.emissiveIntensity)
        ;(
          _object.material as unknown as THREE.MeshPhysicalNodeMaterial
        ).emissiveNode = settings.emissiveNode
      })

    folder
      .addBinding(debugSettings, "emissiveIntensity", {
        min: 0,
        max: 2,
        step: 0.1,
      })
      .on("change", (ev) => {
        ;(settings.emissiveIntensity.value as number) = ev.value
        // Update emissiveNode when intensity changes
        settings.emissiveNode = settings.color.mul(settings.emissiveIntensity)
        ;(
          _object.material as unknown as THREE.MeshPhysicalNodeMaterial
        ).emissiveNode = settings.emissiveNode
      })
  }
}
